import {
  COLS, ROWS, OFF, PIXEL_COUNT, idx,
  Frame, HexColor, AnimationFrame, LivePayload, LedDesign,
} from './led-board.model';

export function normalizeHex(input: string): HexColor {
  if (typeof input !== 'string') { throw new Error('hex must be a string'); }
  let s = input.trim().toLowerCase();
  if (/^#[0-9a-f]{3}$/.test(s)) {
    s = '#' + s[1] + s[1] + s[2] + s[2] + s[3] + s[3];
  }
  if (!/^#[0-9a-f]{6}$/.test(s)) { throw new Error('invalid hex: ' + input); }
  return s;
}

export function isValidFrame(f: any): boolean {
  return Array.isArray(f)
    && f.length === PIXEL_COUNT
    && f.every((c: any) => typeof c === 'string' && /^#[0-9a-f]{6}$/.test(c));
}

export function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

export function floodFill(frame: Frame, x: number, y: number, newColor: HexColor): Frame {
  const out = frame.slice();
  const target = out[idx(x, y)];
  const fill = normalizeHex(newColor);
  if (target === fill) { return out; }
  const stack: Array<[number, number]> = [[x, y]];
  while (stack.length) {
    const [cx, cy] = stack.pop()!;
    if (cx < 0 || cy < 0 || cx >= COLS || cy >= ROWS) { continue; }
    if (out[idx(cx, cy)] !== target) { continue; }
    out[idx(cx, cy)] = fill;
    stack.push([cx + 1, cy], [cx - 1, cy], [cx, cy + 1], [cx, cy - 1]);
  }
  return out;
}

export function buildFrameDesign(name: string, frame: Frame, thumbnail: string, now: number): LedDesign {
  return { name, w: COLS, h: ROWS, type: 'frame', frame: frame.slice(), thumbnail, createdAt: now };
}

export function buildAnimationDesign(
  name: string, frames: AnimationFrame[], loop: boolean, thumbnail: string, now: number,
): LedDesign {
  return {
    name, w: COLS, h: ROWS, type: 'animation',
    frame: frames[0].pixels.slice(),
    animation: { loop, frames: frames.map(f => ({ ms: f.ms, pixels: f.pixels.slice() })) },
    thumbnail, createdAt: now,
  };
}

export function buildFramePayload(frame: Frame, brightness: number, now: number): LivePayload {
  return { mode: 'frame', brightness: clampByte(brightness), updatedAt: now, frame: frame.slice() };
}

export function buildAnimationPayload(frames: AnimationFrame[], loop: boolean, brightness: number, now: number): LivePayload {
  return {
    mode: 'animation', brightness: clampByte(brightness), updatedAt: now,
    animation: { loop, frames: frames.map(f => ({ ms: f.ms, pixels: f.pixels.slice() })) },
  };
}

export function makeThrottledSender(intervalMs: number, sender: (p: LivePayload) => void): (p: LivePayload) => void {
  let last = 0;
  let pending: LivePayload | null = null;
  let timer: any = null;
  const flush = () => {
    if (pending) { last = Date.now(); sender(pending); pending = null; }
    timer = null;
  };
  return (payload: LivePayload) => {
    const now = Date.now();
    if (now - last >= intervalMs && !timer) { last = now; sender(payload); }
    else { pending = payload; if (!timer) { timer = setTimeout(flush, intervalMs); } }
  };
}

export function clampScale(s: number, min = 1, max = 8): number {
  return Math.max(min, Math.min(max, s));
}

export interface Cell { x: number; y: number; }

function inBounds(x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < COLS && y < ROWS;
}
function dedupeInBounds(cells: Cell[]): Cell[] {
  const seen = new Set<number>();
  const out: Cell[] = [];
  for (const c of cells) {
    if (!inBounds(c.x, c.y)) { continue; }
    const k = c.y * COLS + c.x;
    if (seen.has(k)) { continue; }
    seen.add(k);
    out.push({ x: c.x, y: c.y });
  }
  return out;
}

export function rowCells(y: number): Cell[] {
  const out: Cell[] = [];
  for (let x = 0; x < COLS; x++) { out.push({ x, y }); }
  return dedupeInBounds(out);
}

export function colCells(x: number): Cell[] {
  const out: Cell[] = [];
  for (let y = 0; y < ROWS; y++) { out.push({ x, y }); }
  return dedupeInBounds(out);
}

export function lineCells(x0: number, y0: number, x1: number, y1: number): Cell[] {
  const out: Cell[] = [];
  let dx = Math.abs(x1 - x0), dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx - dy, x = x0, y = y0;
  // guard against pathological inputs
  for (let i = 0; i < 5000; i++) {
    out.push({ x, y });
    if (x === x1 && y === y1) { break; }
    const e2 = 2 * err;
    if (e2 > -dy) { err -= dy; x += sx; }
    if (e2 < dx) { err += dx; y += sy; }
  }
  return dedupeInBounds(out);
}

export function rectOutlineCells(x0: number, y0: number, x1: number, y1: number): Cell[] {
  const minX = Math.min(x0, x1), maxX = Math.max(x0, x1);
  const minY = Math.min(y0, y1), maxY = Math.max(y0, y1);
  const out: Cell[] = [];
  for (let x = minX; x <= maxX; x++) { out.push({ x, y: minY }, { x, y: maxY }); }
  for (let y = minY; y <= maxY; y++) { out.push({ x: minX, y }, { x: maxX, y }); }
  return dedupeInBounds(out);
}

export function circleOutlineCells(cx: number, cy: number, ex: number, ey: number): Cell[] {
  const r = Math.round(Math.hypot(ex - cx, ey - cy));
  if (r === 0) { return dedupeInBounds([{ x: cx, y: cy }]); }
  const out: Cell[] = [];
  let x = r, y = 0, err = 1 - r;
  while (x >= y) {
    out.push(
      { x: cx + x, y: cy + y }, { x: cx + y, y: cy + x },
      { x: cx - y, y: cy + x }, { x: cx - x, y: cy + y },
      { x: cx - x, y: cy - y }, { x: cx - y, y: cy - x },
      { x: cx + y, y: cy - x }, { x: cx + x, y: cy - y },
    );
    y++;
    if (err < 0) { err += 2 * y + 1; }
    else { x--; err += 2 * (y - x) + 1; }
  }
  return dedupeInBounds(out);
}

export function cellsForTool(tool: string, sx: number, sy: number, ex: number, ey: number): Cell[] {
  switch (tool) {
    case 'line': return lineCells(sx, sy, ex, ey);
    case 'rect': return rectOutlineCells(sx, sy, ex, ey);
    case 'circle': return circleOutlineCells(sx, sy, ex, ey);
    default: return [];
  }
}

export function applyCells(frame: Frame, cells: Cell[], color: HexColor): Frame {
  const value = normalizeHex(color);
  const out = frame.slice();
  for (const c of cells) {
    if (inBounds(c.x, c.y)) { out[idx(c.x, c.y)] = value; }
  }
  return out;
}

export async function deflateBase64(text: string): Promise<string | null> {
  if (typeof CompressionStream === 'undefined') { return null; }
  const input = new TextEncoder().encode(text);
  const cs = new CompressionStream('deflate');
  const writer = cs.writable.getWriter();
  writer.write(input);
  writer.close();
  const compressed = new Uint8Array(await new Response(cs.readable).arrayBuffer());
  let binary = '';
  for (let i = 0; i < compressed.length; i++) { binary += String.fromCharCode(compressed[i]); }
  return btoa(binary);
}

export async function inflateBase64(b64: string): Promise<string | null> {
  if (typeof DecompressionStream === 'undefined') { return null; }
  try {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) { bytes[i] = binary.charCodeAt(i); }
    const ds = new DecompressionStream('deflate');
    const writer = ds.writable.getWriter();
    writer.write(bytes);
    writer.close();
    return await new Response(ds.readable).text();
  } catch {
    return null;   // truncated, mis-encoded, or not deflate after all
  }
}

/**
 * The single frame that best represents a live payload — frame one of an
 * animation — so the start screen can show what the board is displaying.
 * Returns null when the payload is missing, malformed or can't be inflated.
 */
export async function previewFrameFromPayload(payload: LivePayload | null): Promise<Frame | null> {
  if (!payload) { return null; }
  if (payload.mode === 'frame') {
    if (isValidFrame(payload.frame)) { return payload.frame as Frame; }
    if (payload.enc === 'deflate' && payload.data) {
      const json = await inflateBase64(payload.data);
      if (!json) { return null; }
      try {
        const f = JSON.parse(json);
        return isValidFrame(f) ? f as Frame : null;
      } catch { return null; }
    }
    return null;
  }
  const anim = payload.animation;
  if (!anim) { return null; }
  const first = anim.frames?.[0]?.pixels;
  if (isValidFrame(first)) { return first as Frame; }
  if (anim.enc === 'deflate' && anim.data) {
    const json = await inflateBase64(anim.data);
    if (!json) { return null; }
    try {
      const frames = JSON.parse(json);
      const px = frames?.[0]?.pixels;
      return isValidFrame(px) ? px as Frame : null;
    } catch { return null; }
  }
  return null;
}

export async function compressAnimationPayload(payload: LivePayload): Promise<LivePayload> {
  if (payload.mode !== 'animation' || !payload.animation || !payload.animation.frames) {
    return payload;
  }
  const frames = payload.animation.frames;
  const data = await deflateBase64(JSON.stringify(frames));
  if (data === null) { return payload; }  // CompressionStream unavailable -> raw fallback
  return {
    ...payload,
    animation: { loop: payload.animation.loop, enc: 'deflate', count: frames.length, data },
  };
}

export async function compressFramePayload(payload: LivePayload): Promise<LivePayload> {
  if (payload.mode !== 'frame' || !payload.frame) { return payload; }
  const data = await deflateBase64(JSON.stringify(payload.frame));
  if (data === null) { return payload; }  // CompressionStream unavailable -> raw fallback
  return { mode: 'frame', brightness: payload.brightness, updatedAt: payload.updatedAt, enc: 'deflate', data };
}

function modWrap(n: number, m: number): number { return ((n % m) + m) % m; }

export function shiftFrameWrap(frame: Frame, dx: number, dy: number): Frame {
  const out = new Array(PIXEL_COUNT);
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      out[idx(x, y)] = frame[idx(modWrap(x - dx, COLS), modWrap(y - dy, ROWS))];
    }
  }
  return out;
}

export function shiftCells(
  frame: Frame, selection: Set<number>, dx: number, dy: number,
): { frame: Frame; selection: Set<number> } {
  const out = frame.slice();
  const captured: Array<{ x: number; y: number; color: HexColor }> = [];
  selection.forEach(i => { captured.push({ x: i % COLS, y: Math.floor(i / COLS), color: frame[i] }); });
  selection.forEach(i => { out[i] = OFF; });
  const newSel = new Set<number>();
  for (const c of captured) {
    const tx = c.x + dx, ty = c.y + dy;
    if (tx >= 0 && ty >= 0 && tx < COLS && ty < ROWS) {
      const ti = idx(tx, ty);
      out[ti] = c.color;
      newSel.add(ti);
    }
  }
  return { frame: out, selection: newSel };
}

export function rectFillCells(x0: number, y0: number, x1: number, y1: number): Cell[] {
  const minX = Math.min(x0, x1), maxX = Math.max(x0, x1);
  const minY = Math.min(y0, y1), maxY = Math.max(y0, y1);
  const out: Cell[] = [];
  for (let y = minY; y <= maxY; y++) { for (let x = minX; x <= maxX; x++) { out.push({ x, y }); } }
  return dedupeInBounds(out);
}

export function circleFillCells(cx: number, cy: number, ex: number, ey: number): Cell[] {
  const r = Math.round(Math.hypot(ex - cx, ey - cy));
  const out: Cell[] = [];
  for (let y = cy - r; y <= cy + r; y++) {
    for (let x = cx - r; x <= cx + r; x++) {
      const ddx = x - cx, ddy = y - cy;
      if (ddx * ddx + ddy * ddy <= r * r) { out.push({ x, y }); }
    }
  }
  return dedupeInBounds(out);
}
